import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Constants } from "config/constants";
import { Formik, Field } from "formik";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { addComment, editComment } from "state/slices/comment";
import _get from "lodash/get";
import CommentsList from "components/lib/Global/CommentsList";
import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import { format } from "date-fns";

const AnnualReturns = ({ isViewMode }) => {
  const dispatch = useDispatch();
  const { comments } = useSelector((state) => state.commentsSlice);
  const { company } = useSelector((state) => state.companySlice);
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { transactions } = transactionUnderReview.items;
  const yyyyAnnualReturnsTransaction = transactions.find(
    (trx) =>
      trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS
  );
  const yyyyAnnualReturnsIndex = transactions.findIndex(
    (trx) => trx._id === yyyyAnnualReturnsTransaction?._id
  );
  const yyyyAnnualReturnsUpdateEffectPath = buildUpdateEffectPath(
    yyyyAnnualReturnsIndex
  );
  const yyyyAnnualReturnsComments = comments.filter(
    (comment) =>
      comment.transaction === yyyyAnnualReturnsTransaction?._id &&
      comment.formName === "annualReturns"
  );
  const updateEffectKey = "updateTransactionUnderReview";
  const formHelperKey = "initiateTransactionChange";
  const annualReturnsArray = yyyyAnnualReturnsTransaction.annualReturns;
  const documentData =
    generateDocumentDataFromAnnualReturnsArray(annualReturnsArray);
  const fileUploads =
    generateFileUploadsFromAnnualReturnsArray(annualReturnsArray);

  const handleSaveComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: yyyyAnnualReturnsTransaction?._id,
          },
        })
      );
    }
  };

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={{
          fileUploads,
        }}
        onSubmit={() => null}
      >
        {() => {
          return (
            <div className="p-4">
              {yyyyAnnualReturnsComments.length > 0 && (
                <div className="px-8 pt-5 pb-6 mt-4 bg-white">
                  <CommentsList
                    comments={yyyyAnnualReturnsComments}
                    toggleCommentStatus={true}
                  />
                  <p className="mt-6 text-sm text-primary">
                    Comments: {yyyyAnnualReturnsComments.length} of{" "}
                    {yyyyAnnualReturnsComments.length}
                  </p>
                </div>
              )}
              <div>
                {documentData.map((document, index) => (
                  <div
                    key={index}
                    className="shadow-sm sm:rounded-md sm:overflow-hidden px-4 py-6 space-y-4 bg-gray-50 sm:p-6 mt-4"
                  >
                    <div className=" mb-4">
                      <h2 className="text-4xl font-bold text-gray-800">
                        {new Date(document.dateOfReturn).getFullYear()}
                      </h2>
                      <p className="text-gray-800 font-bold">Annual Returns</p>
                    </div>

                    <FormFieldsCard
                      fields={[
                        {
                          key: "Date of Return",
                          value: format(
                            new Date(document.dateOfReturn),
                            "yyyy/MM/dd"
                          ),
                          name: "dateOfReturn",
                        },
                      ]}
                      resolvedFields={_get(
                        yyyyAnnualReturnsTransaction,
                        "resolvedFields"
                      )}
                      onlyPreview
                      showDocumentLabel
                      addReviewComment={!isViewMode}
                      hideResolveAllCheckbox={isViewMode}
                      handleSaveComment={handleSaveComment}
                      formName="annualReturns"
                      formHelperKey={formHelperKey}
                      updateEffectKey={updateEffectKey}
                      updateEffectPath={yyyyAnnualReturnsUpdateEffectPath}
                      formId={_get(yyyyAnnualReturnsTransaction, "_id")}
                      resolveAllFieldsCheckbox
                      fileUploadComments={yyyyAnnualReturnsComments}
                    />

                    <div className="bg-white p-2">
                      <Field
                        name="fileUploads"
                        component={FileUploadForm}
                        documentData={[documentData[index]]}
                        resolvedFields={_get(
                          yyyyAnnualReturnsTransaction,
                          "resolvedFields"
                        )}
                        onlyPreview
                        showDocumentLabel
                        addReviewComment={!isViewMode}
                        hideResolveAllCheckbox={isViewMode}
                        handleSaveComment={handleSaveComment}
                        formName="annualReturns"
                        formHelperKey={formHelperKey}
                        updateEffectKey={updateEffectKey}
                        updateEffectPath={yyyyAnnualReturnsUpdateEffectPath}
                        formId={_get(yyyyAnnualReturnsTransaction, "_id")}
                        resolveAllFieldsCheckbox
                        fileUploadComments={yyyyAnnualReturnsComments}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="py-2"></div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default AnnualReturns;

function buildUpdateEffectPath(sliceIndex) {
  return `transactionSlice.transactionUnderReview.items.transactions.${sliceIndex}`;
}

function generateFileUploadsFromAnnualReturnsArray(annualReturnsArray) {
  const fileUploads = {};
  annualReturnsArray.forEach((returnObj, index) => {
    fileUploads[`annualReturn${index + 1}`] = returnObj.annualReturn.fileName;
  });
  return fileUploads;
}

function generateDocumentDataFromAnnualReturnsArray(annualReturnsArray) {
  const documentData = [];
  annualReturnsArray.forEach((returnObj, index) => {
    documentData.push({
      label: `Annual Returns`,
      name: `annualReturn${index + 1}`,
      accept: "application/pdf",
      ...returnObj,
    });
  });
  return documentData;
}
